import { FLAT_SCALE, SHARP_SCALE } from './diatonicChords';

// Turning MIDI note numbers into a drawable keyboard. The existing piano components
// (PianoScale, ChordPiano) build their keys from note names because they start from a key or a
// chord symbol; a MIDI clip starts from numbers instead, so the geometry is derived arithmetically
// here rather than by walking a chromatic scale.

// Pitch classes that sit on a raised key, counting from C
const BLACK_PITCH_CLASSES = [1, 3, 6, 8, 10];

const SEMITONES_PER_OCTAVE = 12;
const MIN_MIDI_NOTE = 0;
const MAX_MIDI_NOTE = 127;

// The 88 keys of an acoustic piano, A0 to C8
export const FULL_PIANO_RANGE = { low: 21, high: 108 };

// What an empty clip falls back to, C3 to B4 — the range a part is most likely to land in
const DEFAULT_RANGE = { low: 48, high: 71 };

// Below this the keys get too thin to read a highlight on, so a narrow clip is padded out
const MIN_OCTAVES = 2;

/** Note's position within its octave, 0 (C) to 11 (B). */
export const getPitchClass = (midi) => ((midi % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE;

export const isBlackMidiNote = (midi) => BLACK_PITCH_CLASSES.includes(getPitchClass(midi));

/** Scientific pitch notation, where MIDI 60 is C4 — the same octave numbering @tonejs/midi reports. */
export const getMidiOctave = (midi) => Math.floor(midi / SEMITONES_PER_OCTAVE) - 1;

export const getMidiNoteName = (midi, preferSharps = true) =>
  (preferSharps ? SHARP_SCALE : FLAT_SCALE)[getPitchClass(midi)];

/** Note name with its octave, e.g. "C#4". */
export const getMidiNoteLabel = (midi, preferSharps = true) =>
  `${getMidiNoteName(midi, preferSharps)}${getMidiOctave(midi)}`;

/**
 * Lowest and highest note the clip actually plays.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @returns {{low: number, high: number}|null} null when the clip holds no notes
 */
export function getMidiNoteRange(midiJson) {
  let low = Infinity;
  let high = -Infinity;

  (midiJson?.tracks || []).forEach((track) => {
    (track.notes || []).forEach((note) => {
      if (note.midi < low) low = note.midi;
      if (note.midi > high) high = note.midi;
    });
  });

  return low > high ? null : { low, high };
}

/**
 * The stretch of keyboard to draw for a clip.
 *
 * Defaults to the clip's own range widened to whole octaves rather than the full 88 keys: at the
 * width of a modal, 88 keys leave each white key about 8px across, which is too thin to read a
 * highlight on. Rounding to C–B boundaries keeps the octave labels meaningful and guarantees the
 * range starts and ends on a white key, which is what the black-key positioning assumes.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @param {boolean} fullRange - Draw all 88 keys instead of fitting the clip
 * @returns {{low: number, high: number}}
 */
export function getKeyboardRange(midiJson, fullRange = false) {
  if (fullRange) return FULL_PIANO_RANGE;

  const range = getMidiNoteRange(midiJson);

  if (!range) return DEFAULT_RANGE;

  let low = Math.floor(range.low / SEMITONES_PER_OCTAVE) * SEMITONES_PER_OCTAVE;
  // Clamped before the widening rather than after it: 127 is a G, not a B, so the top octave is
  // incomplete. Clamping afterwards would shorten a range the loop had already widened and hand
  // back less than the minimum it just guaranteed.
  let high = Math.min(
    MAX_MIDI_NOTE,
    Math.floor(range.high / SEMITONES_PER_OCTAVE) * SEMITONES_PER_OCTAVE + SEMITONES_PER_OCTAVE - 1,
  );

  // Widen upwards first: parts sit low more often than high, so the added octave is likelier to
  // be useful above the clip than below it
  while ((high - low + 1) / SEMITONES_PER_OCTAVE < MIN_OCTAVES) {
    if (high + SEMITONES_PER_OCTAVE <= MAX_MIDI_NOTE) high += SEMITONES_PER_OCTAVE;
    else low -= SEMITONES_PER_OCTAVE;
  }

  return { low: Math.max(MIN_MIDI_NOTE, low), high };
}

/**
 * Every key in a range, with what the layout needs to place it.
 *
 * White keys are laid out in order and share the width evenly; black keys are drawn on top,
 * straddling the gap to the left of the white key at `whiteIndex`. That index is the count of
 * white keys before the key, which for a white key is its own position in the row.
 *
 * @param {number} low - Lowest MIDI note, expected to be a white key
 * @param {number} high - Highest MIDI note
 * @param {boolean} preferSharps - Name black keys with sharps rather than flats
 * @returns {{keys: Array<object>, whiteCount: number}}
 */
export function generateKeyboardKeys(low, high, preferSharps = true) {
  const keys = [];
  let whiteCount = 0;

  for (let midi = low; midi <= high; midi++) {
    const isBlack = isBlackMidiNote(midi);

    keys.push({
      midi,
      isBlack,
      whiteIndex: whiteCount,
      note: getMidiNoteName(midi, preferSharps),
      octave: getMidiOctave(midi),
      // The C of each octave carries the label that orients the rest of the keyboard
      isOctaveStart: getPitchClass(midi) === 0,
    });

    if (!isBlack) whiteCount += 1;
  }

  return { keys, whiteCount };
}
